"use client";

import type { Situation } from "@/generated/fastapi";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import {
  Button,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { memo } from "react";

interface ThoughtAnchorTargetItemProps {
  situation: Situation;
  isDisabled?: boolean;
  isCurrent?: boolean;
  isPending?: boolean;
  disabledReason?: string;
  onClick?: () => void;
}

function ThoughtAnchorTargetItem({
  situation,
  isDisabled = false,
  isCurrent = false,
  isPending = false,
  disabledReason,
  onClick,
}: ThoughtAnchorTargetItemProps) {
  const isClickable = !isDisabled && !isCurrent && !isPending && !!onClick;
  const statusMessage = isPending
    ? "Anchoring thoughts to this situation..."
    : isCurrent
      ? "Current anchor for all selected thoughts"
      : disabledReason;

  return (
    <Button
      type="button"
      variant="ghost"
      width="full"
      height="auto"
      minH="auto"
      justifyContent="flex-start"
      alignItems="stretch"
      textAlign="left"
      whiteSpace="normal"
      p="3"
      borderWidth="1px"
      borderRadius="md"
      borderColor={
        isCurrent
          ? "moveon.primary"
          : isDisabled
            ? "moveon.warning"
            : isPending
              ? "moveon.secondary"
              : "border"
      }
      bg={
        isCurrent
          ? "moveon.primaryLight"
          : isDisabled
            ? "bg.subtle"
            : isPending
              ? "bg.muted"
              : "bg.canvas"
      }
      cursor={
        isClickable
          ? "pointer"
          : isDisabled || isCurrent || isPending
            ? "not-allowed"
            : "default"
      }
      opacity={isDisabled && !isCurrent ? 0.6 : 1}
      transition="all 0.2s"
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      _hover={
        isClickable
          ? {
              bg: "bg.muted",
              borderColor: "moveon.primary",
              transform: "translateY(-1px)",
              shadow: "sm",
            }
          : undefined
      }
      _active={
        isClickable
          ? {
              transform: "translateY(0)",
              shadow: "none",
            }
          : undefined
      }
      aria-label={`Anchor selected thoughts to ${situation.position}`}
    >
      <VStack align="stretch" gap="2" width="full">
        <HStack justify="space-between" align="start" gap="3">
          <VStack align="stretch" gap="1" minW="0">
            <Heading size="md" lineClamp={2}>
              {situation.position}
            </Heading>
            <Heading size="sm" color="moveon.text.muted" lineClamp={2}>
              {situation.context}
            </Heading>
          </VStack>
          {isPending ? (
            <Spinner size="sm" color="moveon.secondary" mt="1" />
          ) : (
            <GenericIcon
              icon={isCurrent ? "situation" : "anchor"}
              size="md"
              color={isCurrent ? "moveon.primary" : "moveon.text.muted"}
            />
          )}
        </HStack>

        {statusMessage ? (
          <Text
            fontSize="xs"
            color={
              isPending
                ? "moveon.secondary"
                : isCurrent
                  ? "moveon.primary"
                  : "moveon.warning"
            }
            fontWeight="medium"
          >
            {statusMessage}
          </Text>
        ) : null}
      </VStack>
    </Button>
  );
}

export default memo(ThoughtAnchorTargetItem);
